
import { UserRole, AgeGroup } from '@/types/user';

export interface RolePermissionConfig {
  role: UserRole;
  age_group: AgeGroup;
  requires_fee: boolean;
  requires_approval: boolean;
  available: boolean;
  account_tier: 'free' | 'paid';
}

export const getRolePermissions = (role: UserRole, ageGroup: AgeGroup): RolePermissionConfig => {
  const permissions: Record<string, Record<string, RolePermissionConfig>> = {
    'player': {
      '13-and-under': { role: 'player', age_group: '13-and-under', requires_fee: false, requires_approval: false, available: true, account_tier: 'free' },
      '14-17': { role: 'player', age_group: '14-17', requires_fee: false, requires_approval: false, available: true, account_tier: 'free' },
      '18-plus': { role: 'player', age_group: '18-plus', requires_fee: false, requires_approval: false, available: true, account_tier: 'free' }
    },
    'coach': {
      '13-and-under': { role: 'coach', age_group: '13-and-under', requires_fee: false, requires_approval: false, available: false, account_tier: 'paid' },
      '14-17': { role: 'coach', age_group: '14-17', requires_fee: true, requires_approval: true, available: true, account_tier: 'paid' },
      '18-plus': { role: 'coach', age_group: '18-plus', requires_fee: true, requires_approval: false, available: true, account_tier: 'paid' }
    },
    'team-owner': {
      '13-and-under': { role: 'team-owner', age_group: '13-and-under', requires_fee: false, requires_approval: false, available: false, account_tier: 'paid' },
      '14-17': { role: 'team-owner', age_group: '14-17', requires_fee: true, requires_approval: true, available: true, account_tier: 'paid' },
      '18-plus': { role: 'team-owner', age_group: '18-plus', requires_fee: true, requires_approval: false, available: true, account_tier: 'paid' }
    },
    'venue': {
      '13-and-under': { role: 'venue', age_group: '13-and-under', requires_fee: false, requires_approval: false, available: false, account_tier: 'paid' },
      '14-17': { role: 'venue', age_group: '14-17', requires_fee: true, requires_approval: true, available: true, account_tier: 'paid' },
      '18-plus': { role: 'venue', age_group: '18-plus', requires_fee: true, requires_approval: false, available: true, account_tier: 'paid' }
    },
    'creative': {
      '13-and-under': { role: 'creative', age_group: '13-and-under', requires_fee: false, requires_approval: false, available: true, account_tier: 'free' },
      '14-17': { role: 'creative', age_group: '14-17', requires_fee: false, requires_approval: false, available: true, account_tier: 'free' },
      '18-plus': { role: 'creative', age_group: '18-plus', requires_fee: false, requires_approval: false, available: true, account_tier: 'free' }
    },
    'support': {
      '13-and-under': { role: 'support', age_group: '13-and-under', requires_fee: false, requires_approval: false, available: true, account_tier: 'free' },
      '14-17': { role: 'support', age_group: '14-17', requires_fee: false, requires_approval: false, available: true, account_tier: 'free' },
      '18-plus': { role: 'support', age_group: '18-plus', requires_fee: false, requires_approval: false, available: true, account_tier: 'free' }
    },
    'fan': {
      '13-and-under': { role: 'fan', age_group: '13-and-under', requires_fee: false, requires_approval: false, available: true, account_tier: 'free' },
      '14-17': { role: 'fan', age_group: '14-17', requires_fee: false, requires_approval: false, available: true, account_tier: 'free' },
      '18-plus': { role: 'fan', age_group: '18-plus', requires_fee: false, requires_approval: false, available: true, account_tier: 'free' }
    },
    'parent': {
      '13-and-under': { role: 'parent', age_group: '13-and-under', requires_fee: false, requires_approval: false, available: false, account_tier: 'free' },
      '14-17': { role: 'parent', age_group: '14-17', requires_fee: false, requires_approval: false, available: false, account_tier: 'free' },
      '18-plus': { role: 'parent', age_group: '18-plus', requires_fee: false, requires_approval: false, available: true, account_tier: 'free' }
    },
    'child': {
      '13-and-under': { role: 'child', age_group: '13-and-under', requires_fee: false, requires_approval: false, available: true, account_tier: 'free' },
      '14-17': { role: 'child', age_group: '14-17', requires_fee: false, requires_approval: false, available: true, account_tier: 'free' },
      '18-plus': { role: 'child', age_group: '18-plus', requires_fee: false, requires_approval: false, available: false, account_tier: 'free' }
    },
    'admin': {
      '13-and-under': { role: 'admin', age_group: '13-and-under', requires_fee: false, requires_approval: false, available: false, account_tier: 'free' },
      '14-17': { role: 'admin', age_group: '14-17', requires_fee: false, requires_approval: false, available: false, account_tier: 'free' },
      '18-plus': { role: 'admin', age_group: '18-plus', requires_fee: false, requires_approval: false, available: true, account_tier: 'free' }
    }
  };

  return permissions[role]?.[ageGroup] || { 
    role, 
    age_group: ageGroup, 
    requires_fee: false, 
    requires_approval: false, 
    available: false,
    account_tier: 'free'
  };
};

export const getAvailableRoles = (ageGroup: AgeGroup): UserRole[] => {
  const allRoles: UserRole[] = ['player', 'coach', 'team-owner', 'venue', 'creative', 'support', 'fan', 'parent', 'child', 'admin'];
  
  return allRoles.filter(role => {
    const config = getRolePermissions(role, ageGroup);
    return config.available;
  });
};

export const getFreeRoles = (): UserRole[] => {
  return ['player', 'fan', 'parent', 'child', 'support', 'creative', 'admin'];
};

export const getPaidRoles = (): UserRole[] => {
  return ['coach', 'team-owner', 'venue'];
};

export const isAccountTierFree = (role: UserRole): boolean => {
  return getFreeRoles().includes(role);
};

export const isAccountTierPaid = (role: UserRole): boolean => {
  return getPaidRoles().includes(role);
};
